import { Modal, TrackedButton } from '@automata/ui';
import { PlannerOptionsInput } from '@automata/wfb-api/src/apis/wfbApiSchemas';
import { Stack } from '@mui/system';
import { useCallback, useEffect, useState } from 'react';

import PlanWorkflowAccordionGroup from './PlanWorkflowAccordionGroup/PlanWorkflowAccordionGroup';
import { getFieldErrorsForTasks } from '../../features/actions';
import { useAppDispatch } from '../../hooks';
import { useValidBatches } from '../../hooks/useValidBatches';
import { PreviewBlocker } from '../../text';
import { useParametersForm } from '../../utils';
import { toPreviewBlockers } from '../../utils/previewBlockers';
import BatchesInput from '../BatchesInput';
import ReplanWarning from '../ReplanWarning';

interface PlanWorkflowModalProps {
  label: string;
  workflowName: string;
  onClose: () => void;
  onSubmit: ({
    batches,
    optimisationTarget,
  }: {
    batches: number;
    optimisationTarget: PlannerOptionsInput['optimisation_target'];
  }) => void;
}

export const PlanWorkflowModal = ({
  label,
  workflowName,
  onSubmit,
  onClose,
}: PlanWorkflowModalProps) => {
  const dispatch = useAppDispatch();
  const { minBatch, validBatches } = useValidBatches();
  const [batches, setBatches] = useState<number | null>(minBatch);
  const [optimizationTarget, setOptimizationTarget] = useState<
    PlannerOptionsInput['optimisation_target']
  >('minimize_total_plan_duration');
  const [blockers, setBlockers] = useState<PreviewBlocker[]>([]);
  const {
    isParametersValid,
    parameters,
    parametersWithoutFileType,
    handleSaveParameter,
  } = useParametersForm();
  const isPlan = label === 'Plan';

  const handleSubmit = useCallback(() => {
    const parsedBatches = Number(batches);

    if (isNaN(parsedBatches)) return;

    onSubmit({
      batches: parsedBatches,
      optimisationTarget: optimizationTarget,
    });
  }, [batches, onSubmit, optimizationTarget]);

  // validate parameters
  useEffect(() => {
    const errors = dispatch(getFieldErrorsForTasks(parameters));

    if (!errors || errors.length === 0) {
      return setBlockers([]);
    }

    setBlockers(toPreviewBlockers({ isError: true, errors }));
  }, [parameters, dispatch]);

  return (
    <Modal onClose={onClose} open title={`${label} "${workflowName}"`}>
      <Stack spacing={2}>
        <Stack spacing={2}>
          <BatchesInput handleChange={setBatches} batches={batches} />
        </Stack>
        {isPlan && (
          <PlanWorkflowAccordionGroup
            optimizationTarget={optimizationTarget}
            parameters={parameters}
            parametersWithoutFileType={parametersWithoutFileType}
            handleSaveParameter={handleSaveParameter}
            handleOptimizationTargetChange={setOptimizationTarget}
          />
        )}
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <TrackedButton
            trackLabel="workflowBuilder.planModal.planCancel"
            color="secondary"
            variant="text"
            onClick={() => {
              onClose();
              setBatches(minBatch);
            }}
          >
            Cancel
          </TrackedButton>
          <Stack direction="row" alignItems="center" gap={1}>
            <TrackedButton
              trackLabel="workflowBuilder.planModal.planCancel"
              variant="contained"
              type="submit"
              disabled={
                !isParametersValid ||
                batches === null ||
                !validBatches.includes(batches ?? 0) ||
                (isPlan && blockers.length > 0)
              }
              onClick={handleSubmit}
            >
              {label}
            </TrackedButton>
            {isPlan && (
              <ReplanWarning
                previewBlockers={blockers}
                title="Something blocks you from replanning."
              />
            )}
          </Stack>
        </Stack>
      </Stack>
    </Modal>
  );
};
